import { computed, type ComputedRef } from 'vue';
import { useRoute } from 'vue-router';
import { type MenuSection } from '@/routers/routeMeta';
import { NavigationMenu } from '@/types';
import { shouldUseNextLayout } from '@/utils/composables/useLayoutPreference';
import { featureFlags } from '@/featureFlags';

/** 現在のルートの左メニューセクション。左メニューを出さないページでは undefined */
export const useMenuSection = (): ComputedRef<MenuSection | undefined> => {
  const route = useRoute();

  return computed(() => route.meta.menuSection ?? undefined);
};

/**
 * 販売プランで情報収集エージェントの設定にまとめる画面。
 * 個人の設定はマイページと同じセクションにあるので、ルート名で選り分ける。
 * マイページ自体は情報収集エージェントの中に残すため含めない。
 *
 * ANDEV_10131_CAPABILITY の削除時は、featureFlags の条件だけを外す。
 */
const SETTINGS_SECTIONS: MenuSection[] = ['admin', 'settings'];
const PERSONAL_SETTINGS_ROUTE_NAMES = [
  'userSettingsEmail',
  'userSettingsNews',
  'userSettingsNotification',
  'userSettingsMark',
];

export const useIsInformationRetrievalSettings = (): ComputedRef<boolean> => {
  const route = useRoute();
  const section = useMenuSection();

  // 旧デザインは販売プラン対応前のメニューのままにする
  const isNextLayout = shouldUseNextLayout();

  return computed(
    () =>
      featureFlags.ANDEV_10131_CAPABILITY &&
      isNextLayout &&
      ((!!section.value && SETTINGS_SECTIONS.includes(section.value)) ||
        PERSONAL_SETTINGS_ROUTE_NAMES.includes(String(route.name ?? ''))),
  );
};

export const useActiveMenu = (): ComputedRef<NavigationMenu> => {
  const section = useMenuSection();
  // 設定はヘッダーの設定メニューから開くため、レールのどの項目にも属さない
  const isInformationRetrievalSettings = useIsInformationRetrievalSettings();

  return computed(() =>
    section.value === undefined ||
    section.value === 'search' ||
    isInformationRetrievalSettings.value
      ? ''
      : section.value,
  );
};
